/**
 * Client-side HTML sanitisation — the read layer.
 *
 * ============================================================================
 *  THIS IS DEFENCE IN DEPTH ON TOP OF SERVER-SIDE WRITE SANITISATION,
 *  NOT A REPLACEMENT FOR IT.
 * ============================================================================
 *
 * `api.privategym`'s `App\Services\HtmlSanitiser` cleans author HTML once, on
 * write, at the CRUD boundary. That remains the primary control. This module
 * exists because it is currently the ONLY other one:
 *
 *   - the app's Content-Security-Policy is stock Cordova boilerplate —
 *     `default-src * 'self' 'unsafe-inline' 'unsafe-eval' data: gap: content:`
 *     — which permits inline script and `eval`, so the CSP stops nothing; and
 *   - `capacitor.config.json` sets `allowNavigation: ["*"]`, so a link that
 *     opens in the MAIN WebView can replace the app with an attacker-controlled
 *     page inside the app's own context.
 *
 * The WebView holds the member's bearer token — the token that opens the gym
 * door. There is no third layer behind this one.
 *
 * What it actually buys, stated honestly:
 *   - legacy rows written before write-side sanitisation existed,
 *   - anything that reaches the database bypassing the write path (the owner
 *     still edits `news` rows directly in MySQL), and
 *   - any future writer that forgets to call the server-side seam.
 *
 * ----------------------------------------------------------------------------
 * THE ALLOWLIST IS IMPORTED, NEVER RESTATED.
 * ----------------------------------------------------------------------------
 * `./html-allowlist.json` is a GENERATED artefact. Its provenance is
 * `php artisan content:allowlist <profile> --json` in `api.privategym`, which
 * derives it from `config/purifier.php` — the same file the server sanitises
 * with. Two sanitisers with two hand-maintained allowlists drift silently, and
 * the read layer then starts permitting what the server strips. Do not type a
 * tag, an attribute or a scheme into this file; regenerate the JSON instead.
 *
 * `tests/js/sanitise.test.js` pins that with a parity test, and 43-03's
 * acceptance criteria include a cross-repo `diff` of the generated file against
 * the command's live output.
 *
 * @see api.privategym/config/purifier.php
 * @see api.privategym/app/Services/HtmlSanitiser.php
 */

import DOMPurify from 'dompurify';
import allowlist from './html-allowlist.json';

/** Profile keys. These are the `html-allowlist.json` top-level keys, and the
 *  values of `HtmlSanitiser::NEWS` / `::USER_NOTES` / `::EXERCISE` server-side. */
const NEWS = 'news';
const USER_NOTES = 'user_notes';
const EXERCISE = 'exercise_description';

/** Built DOMPurify configs, memoised per profile (see `configFor`). */
const configCache = {};

/**
 * The union of every attribute the profile permits on any tag.
 *
 * DELIBERATE, DOCUMENTED WIDENING. HTMLPurifier's allowlist is PER TAG
 * (`a[href|title|target|rel]`), but DOMPurify's `ALLOWED_ATTR` is GLOBAL — it
 * has no per-tag form. Flattening therefore makes `href` permissible on any
 * allowed tag rather than only on `<a>`, and `class` permissible on any allowed
 * tag rather than only on `<ol>`/`<ul>`.
 *
 * That is safe here and the cost of closing it is not worth paying:
 *   - no other tag in any of the three profiles interprets `href` — none of
 *     `p, div, br, strong, b, em, i, u, s, strike, sub, sup, ul, ol, li, h2,
 *     h3, blockquote` has link semantics, so a stray `href` is inert markup;
 *   - `class` is narrowed by value anyway (see `narrowClasses`), so widening
 *     WHICH tags may carry it cannot widen WHAT it may say; and
 *   - the alternative is a per-tag `uponSanitizeAttribute` hook, which buys no
 *     measurable safety and adds a second place for the two allowlists to
 *     disagree.
 *
 * The divergence from the server profile is written down here rather than left
 * silent, because "the client is slightly wider than the server" is exactly the
 * kind of fact that has to be discoverable when somebody adds a tag later.
 *
 * @param {Object.<string, string[]>} attributes  the profile's per-tag map
 * @returns {string[]} every distinct attribute name, sorted
 */
function flattenedAttributes(attributes) {
  const union = new Set();

  Object.keys(attributes).forEach((tag) => {
    attributes[tag].forEach((attribute) => union.add(attribute));
  });

  return Array.from(union).sort();
}

/**
 * Builds the URI allowlist regexp from the profile's own `schemes` array, so
 * the permitted schemes come from the same generated source as everything else.
 *
 * Three branches, matching DOMPurify's own default in shape but NOT in content
 * (its default additionally permits `tel:`, `callto:`, `sms:`, `cid:`, `xmpp:`
 * and `ftp:`, none of which any of these profiles allows):
 *
 *   1. `(?:https|http|mailto):`  — the profile's absolute schemes.
 *   2. `[^a-z]`                  — anything not starting with a letter, i.e.
 *                                  `/path`, `#anchor`, `?q=`, `1234`.
 *   3. `[a-z+.\-]+(?:[^a-z+.\-:]|$)` — a leading word that is provably NOT a
 *                                  scheme, because the character after it is
 *                                  not a colon. `relative/page` matches here;
 *                                  `javascript:alert(1)` does not, because the
 *                                  run of scheme characters IS followed by `:`.
 *
 * Branches 2 and 3 exist to keep RELATIVE hrefs, which HTMLPurifier also keeps
 * (`URI.AllowedSchemes` only governs absolute URIs). Dropping them would make
 * the client narrower than the server and silently strip a working link.
 *
 * `javascript:`, `data:` and `vbscript:` are all rejected — each is a run of
 * scheme characters followed by a colon, and none is in any profile's list. The
 * `i` flag makes `JaVaScRiPt:` fail identically; DOMPurify additionally strips
 * whitespace and control characters from the value before testing, so
 * `java\tscript:` cannot slip past either.
 *
 * @param {string[]} schemes  the profile's permitted absolute schemes
 * @returns {RegExp}
 */
function schemeRegExp(schemes) {
  const escaped = schemes.map((scheme) => scheme.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

  return new RegExp(
    `^(?:(?:${escaped.join('|')}):|[^a-z]|[a-z+.\\-]+(?:[^a-z+.\\-:]|$))`,
    'i',
  );
}

/**
 * Builds — and memoises — the DOMPurify config for one profile.
 *
 * Every value is derived from `html-allowlist.json`. Nothing here is a literal
 * tag, attribute or scheme; that is the whole point of the module.
 *
 * @param {string} profileName
 * @returns {Object} a DOMPurify config object
 */
function configFor(profileName) {
  if (!configCache[profileName]) {
    const profile = allowlist[profileName];

    configCache[profileName] = {
      // `.slice()` so DOMPurify can never reach the shared imported arrays.
      ALLOWED_TAGS: profile.tags.slice(),
      ALLOWED_ATTR: flattenedAttributes(profile.attributes),
      ALLOWED_URI_REGEXP: schemeRegExp(profile.schemes),

      // Unwrap a disallowed element but keep its text, rather than deleting
      // both. This mirrors HTMLPurifier, whose behaviour 43-01 measured
      // directly: `<h1>een</h1>` comes back as `een` — the heading is lost, the
      // words are not. It also matches `AutoFormat.RemoveEmpty => false` on
      // legitimate content. DOMPurify still deletes the CONTENTS of genuinely
      // dangerous wrappers (`script`, `style`, `iframe`, `noscript`, `svg`,
      // `math`, `template`, ...) via its own FORBID_CONTENTS list, which is
      // what makes the mXSS payload safe rather than merely unwrapped.
      KEEP_CONTENT: true,

      // Return a string. Stated rather than assumed, because every call site is
      // a `v-html` binding and a DOM node there would render as "[object ...]".
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
    };
  }

  return configCache[profileName];
}

/**
 * Link hardening — mirrors the server's `HTML.TargetBlank` + `HTML.Nofollow`.
 *
 * NOT cosmetic, and not an SEO measure. `capacitor.config.json` sets
 * `allowNavigation: ["*"]`, so a link left to open in the MAIN WebView replaces
 * the running app with an attacker-controlled page inside the app's own origin
 * and session — with the door-opening bearer token still in `localStorage`.
 * `target="_blank"` sends it to the system browser instead; `noopener` denies
 * the opened page a handle back to `window.opener`.
 *
 * Runs in `afterSanitizeAttributes`, i.e. after DOMPurify has already thrown
 * away disallowed attributes and rejected disallowed URI schemes — so a
 * `javascript:` link has NO `href` left by the time this sees it and is
 * correctly left alone rather than being handed a `target`.
 *
 * @param {Element} node
 */
function hardenLinks(node) {
  if (typeof node.hasAttribute !== 'function' || !node.hasAttribute('href')) {
    return;
  }

  node.setAttribute('target', '_blank');
  node.setAttribute('rel', 'nofollow noreferrer noopener');
}

/**
 * Class narrowing — mirrors the server's `Attr.AllowedClasses`.
 *
 * `exercise_description` permits `ol[class]` but only the literal value
 * `steps-list` (`api/app/Console/Commands/Exercises/UpdateExercisesTable.php`
 * emits exactly that markup and re-emits it on every run). Permitting the
 * attribute without constraining its value would allow any class at all, which
 * under this app's CSS is a layout-and-overlay primitive.
 *
 * The permitted values come from the profile's own `classes` array in the
 * generated JSON — 43-01 added that fifth key for precisely this reason — and
 * never from a literal here.
 *
 * @param {Element} node
 * @param {string[]} allowedClasses  the profile's permitted class tokens
 */
function narrowClasses(node, allowedClasses) {
  if (typeof node.hasAttribute !== 'function' || !node.hasAttribute('class')) {
    return;
  }

  const kept = node
    .getAttribute('class')
    .split(/\s+/)
    .filter((token) => token !== '' && allowedClasses.indexOf(token) !== -1);

  if (kept.length === 0) {
    node.removeAttribute('class');
    return;
  }

  node.setAttribute('class', kept.join(' '));
}

/**
 * Sanitises one string against one profile.
 *
 * Hooks are registered immediately before the call and removed immediately
 * after, inside a `finally` so a throw cannot leave one armed. They are scoped
 * per call on purpose: a globally registered link-hardening hook would also fire
 * for `user_notes` and `exercise_description`, which is harmless today (neither
 * profile allows `href`) but is the sort of cross-profile coupling that later
 * becomes a bug nobody can locate.
 *
 * Nothing else in this app registers DOMPurify hooks, so `removeAllHooks()` is
 * safe; if that ever changes, switch to `removeHook('afterSanitizeAttributes')`.
 *
 * @param {*} html          untrusted markup; anything non-string is treated as empty
 * @param {string} profileName
 * @returns {string} sanitised markup, or `''`
 */
function sanitise(html, profileName) {
  if (typeof html !== 'string' || html === '') {
    return '';
  }

  const allowedClasses = allowlist[profileName].classes;

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    hardenLinks(node);
    narrowClasses(node, allowedClasses);
  });

  try {
    return DOMPurify.sanitize(html, configFor(profileName));
  } finally {
    DOMPurify.removeAllHooks();
  }
}

/**
 * Sanitises news article content for `v-html`.
 *
 * @param {*} html
 * @returns {string}
 */
export function sanitiseNewsHtml(html) {
  return sanitise(html, NEWS);
}

/**
 * Sanitises an admin's notes on a member (`users.notes`) for `v-html`.
 *
 * @param {*} html
 * @returns {string}
 */
export function sanitiseNotesHtml(html) {
  return sanitise(html, USER_NOTES);
}

/**
 * Sanitises an exercise description (`exercises.description`) for `v-html`.
 *
 * @param {*} html
 * @returns {string}
 */
export function sanitiseExerciseHtml(html) {
  return sanitise(html, EXERCISE);
}
